"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  bannerText,
  type LetterGap,
  type LetterStyle,
  MAX_BANNER_CHARACTERS,
  spellLetters,
  type WordMode,
  wordsToEmoji,
} from "@/lib/tool-kit/random-fun/emoji-text";

/** English strings. A translated content file overrides these through `ui`. */
export const EMOJI_TEXT_GENERATOR_STRINGS = {
  inputLabel: "Your text",
  inputPlaceholder: "Type or paste your text",
  inputHint: "Block letters use up to {max} characters per line.",
  sampleLetters: "Hello world",
  sampleBlocks: "Hi!",
  sampleWords: "I love pizza and my cat",
  modeLabel: "What to make",
  modeLetters: "Letter symbols",
  modeBlocks: "Big block letters",
  modeWords: "Words to emoji",
  tryExample: "Try an example",
  styleLabel: "Letter style",
  styleRegional:
    "Emoji letters (flag-style \u{1F1E6} \u{1F1E7} \u{1F1E8}, numbers as keycaps)",
  styleSquared: "Squared letters 🄰 🄱 🄲",
  styleNegative: "Black squared letters 🅰 🅱 🅲",
  gapLabel: "Between letters",
  gapInvisible: "Invisible separator (stops letters forming flags)",
  gapNone: "Nothing",
  gapSpace: "A space",
  fgLabel: "Letter emoji",
  bgLabel: "Background emoji",
  fgPick: "Use {emoji} for the letters",
  bgPick: "Use {emoji} for the background",
  perLineLabel: "Letters per line",
  wordModeLabel: "Words become",
  wordReplace: "Replace the word with its emoji",
  wordAfter: "Keep the word and add the emoji after it",
  output: "Result",
  outputLabel: "Your emoji text",
  copy: "Copy",
  copied: "Copied!",
  empty: "Type some text above to see the result.",
  statCharacters: "Characters",
  statMatched: "Words changed",
  statSquares: "Emoji used",
  limitNote:
    "This is {count} characters. Many chat apps cap a message at around 2,000 (Discord's limit for most accounts), so it may need splitting.",
  unchangedNote:
    "These characters have no symbol in this style and were left as typed: {list}",
  unsupportedNote:
    "These characters have no block letter and were left out: {list}",
  noMatchNote:
    "None of the words in your text is in the emoji list. Try common words such as love, cat, pizza, sun or hello.",
  wordsNote:
    "{count} {wordWord} changed. The list covers common nouns, feelings and greetings; plurals work too.",
  wordWord: "one: word|other: words",
} as const;

type Mode = "letters" | "blocks" | "words";

/** Quick colours for block letters. */
const PALETTE = [
  "⬛",
  "⬜",
  "\u{1F7E5}",
  "\u{1F7E7}",
  "\u{1F7E8}",
  "\u{1F7E9}",
  "\u{1F7E6}",
  "\u{1F7EA}",
  "\u{1F7EB}",
  "❤️",
  "⭐",
  "\u{1F525}",
];
const DEFAULT_FG = "\u{1F7E6}";
const DEFAULT_BG = "⬜";
const MAX_TOKEN = 12;
const DISCORD_LIMIT = 2000;

function PalettePicker({
  label,
  pick,
  onPick,
}: {
  label: string;
  pick: string;
  onPick: (emoji: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {PALETTE.map((emoji) => (
        <button
          aria-label={t(pick, { emoji })}
          className={BUTTON_SMALL}
          key={emoji}
          onClick={() => onPick(emoji)}
          title={label}
          type="button"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

type Strings = typeof EMOJI_TEXT_GENERATOR_STRINGS;

interface ConvertInput {
  mode: Mode;
  text: string;
  style: LetterStyle;
  gap: LetterGap;
  fg: string;
  bg: string;
  perLine: number;
  wordMode: WordMode;
  strings: Strings;
  locale: string;
  fmt: (value: number) => string;
}

interface Converted {
  output: string;
  notes: string[];
  tally: { label: string; value: number };
}

function convertWords(input: ConvertInput): Converted {
  const { strings, locale, fmt } = input;
  const result = wordsToEmoji(input.text, input.wordMode);
  const note =
    result.matched === 0
      ? strings.noMatchNote
      : t(strings.wordsNote, {
          count: fmt(result.matched),
          wordWord: tPlural(strings.wordWord, locale, result.matched),
        });
  return {
    output: result.output,
    notes: [note],
    tally: { label: strings.statMatched, value: result.matched },
  };
}

function convertBlocks(input: ConvertInput): Converted {
  const { strings } = input;
  const on = input.fg.trim().slice(0, MAX_TOKEN) || DEFAULT_FG;
  const off = input.bg.trim().slice(0, MAX_TOKEN) || DEFAULT_BG;
  const banner = bannerText(input.text, on, off, input.perLine);
  const notes =
    banner.unsupported.length > 0
      ? [t(strings.unsupportedNote, { list: banner.unsupported.join(" ") })]
      : [];
  return {
    output: banner.output,
    notes,
    tally: { label: strings.statSquares, value: banner.squares },
  };
}

function convertLetters(input: ConvertInput): Converted {
  const spelled = spellLetters(input.text, input.style, input.gap);
  const notes =
    spelled.unchanged.length > 0
      ? [t(input.strings.unchangedNote, { list: spelled.unchanged.join(" ") })]
      : [];
  return {
    output: spelled.output,
    notes,
    tally: {
      label: input.strings.statCharacters,
      value: spelled.output.length,
    },
  };
}

/** The result for the current settings, with any notes the reader should see. */
function convert(input: ConvertInput): Converted {
  if (input.text.trim() === "") {
    return {
      output: "",
      notes: [],
      tally: { label: input.strings.statCharacters, value: 0 },
    };
  }
  let converted: Converted;
  if (input.mode === "letters") {
    converted = convertLetters(input);
  } else if (input.mode === "blocks") {
    converted = convertBlocks(input);
  } else {
    converted = convertWords(input);
  }
  if (converted.output.length > DISCORD_LIMIT) {
    converted.notes.push(
      t(input.strings.limitNote, { count: input.fmt(converted.output.length) })
    );
  }
  return converted;
}

export default function EmojiTextGeneratorWidget() {
  const strings = useToolUi(EMOJI_TEXT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("letters");
  const [text, setText] = useState<string>(strings.sampleLetters);
  const [style, setStyle] = useState<LetterStyle>("regional");
  const [gap, setGap] = useState<LetterGap>("invisible");
  const [fg, setFg] = useState(DEFAULT_FG);
  const [bg, setBg] = useState(DEFAULT_BG);
  const [perLine, setPerLine] = useState("5");
  const [wordMode, setWordMode] = useState<WordMode>("replace");

  function changeMode(next: Mode) {
    setMode(next);
    const sample = {
      letters: strings.sampleLetters,
      blocks: strings.sampleBlocks,
      words: strings.sampleWords,
    }[next];
    setText(sample);
  }

  const { output, notes, tally } = convert({
    mode,
    text,
    style,
    gap,
    fg,
    bg,
    perLine: Number(perLine),
    wordMode,
    strings,
    locale,
    fmt,
  });
  const length = output.length;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="etg-mode"
        onChange={changeMode}
        options={[
          { value: "letters", label: strings.modeLetters },
          { value: "blocks", label: strings.modeBlocks },
          { value: "words", label: strings.modeWords },
        ]}
        value={mode}
      />
      <TextAreaField
        hint={
          mode === "blocks"
            ? t(strings.inputHint, { max: fmt(MAX_BANNER_CHARACTERS) })
            : undefined
        }
        id="etg-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={3}
        value={text}
      />

      {mode === "letters" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="etg-style"
            label={strings.styleLabel}
            onChange={setStyle}
            options={[
              { value: "regional", label: strings.styleRegional },
              { value: "squared", label: strings.styleSquared },
              { value: "negative", label: strings.styleNegative },
            ]}
            value={style}
          />
          <SelectField
            id="etg-gap"
            label={strings.gapLabel}
            onChange={setGap}
            options={[
              { value: "invisible", label: strings.gapInvisible },
              { value: "none", label: strings.gapNone },
              { value: "space", label: strings.gapSpace },
            ]}
            value={gap}
          />
        </div>
      ) : null}

      {mode === "blocks" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <TextField
                id="etg-fg"
                label={strings.fgLabel}
                onChange={setFg}
                value={fg}
              />
              <PalettePicker
                label={strings.fgLabel}
                onPick={setFg}
                pick={strings.fgPick}
              />
            </div>
            <div className="space-y-2">
              <TextField
                id="etg-bg"
                label={strings.bgLabel}
                onChange={setBg}
                value={bg}
              />
              <PalettePicker
                label={strings.bgLabel}
                onPick={setBg}
                pick={strings.bgPick}
              />
            </div>
            <SelectField
              id="etg-perline"
              label={strings.perLineLabel}
              onChange={setPerLine}
              options={[3, 4, 5, 6, 8].map((n) => ({
                value: String(n),
                label: fmt(n),
              }))}
              value={perLine}
            />
          </div>
        </div>
      ) : null}

      {mode === "words" ? (
        <SelectField
          id="etg-wordmode"
          label={strings.wordModeLabel}
          onChange={setWordMode}
          options={[
            { value: "replace", label: strings.wordReplace },
            { value: "after", label: strings.wordAfter },
          ]}
          value={wordMode}
        />
      ) : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.output}</h2>
        {output === "" ? (
          <Notice>{strings.empty}</Notice>
        ) : (
          <>
            <TextAreaField
              id="etg-output"
              label={strings.outputLabel}
              onChange={() => undefined}
              prose={mode !== "blocks"}
              rows={mode === "blocks" ? 8 : 4}
              value={output}
            />
            <div className="flex flex-wrap items-center gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={output}
              />
            </div>
            <StatGrid>
              <StatCard label={strings.statCharacters} value={fmt(length)} />
              {mode === "letters" ? null : (
                <StatCard label={tally.label} value={fmt(tally.value)} />
              )}
            </StatGrid>
            {notes.map((note) => (
              <p className={HINT} key={note}>
                {note}
              </p>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
